import { defineStore } from 'pinia';
import { CategoryCode, SubCategoryCode, } from 'config-request/src/interfaces/feature-list';
// eslint-disable-next-line import/no-cycle
import { useFeatureListStore } from './feature-list';
import { useModalsStore } from './modals';
import { useRuleCheckStore } from './rule-check';
import { MultipleKeyName } from '../types';
import { concatOptionsList, customStorage } from '../utils';
function isIn(key, obj) {
    return typeof obj === 'object' && key in obj;
}
let markedConfig = {};
const keyNameList = [
    MultipleKeyName.exteriorCustom,
    MultipleKeyName.interiorCustom,
    MultipleKeyName.optionalConfig,
    MultipleKeyName.additionalConfig,
];
const initCurrentOpts = {
    /**
     * 当前车型
     */
    model_code: '',
    /**
     * 当前选中的版本
     */
    generation_code: '',
    /**
     * 当前选中的电池容量
     */
    F00019: '',
    /**
     * 当前选中的电池购买方式
     */
    F00029: '',
    /**
     * 当前选中的轮圈
     */
    F00003: '',
    /**
     * 当前选中的颜色
     */
    F00002: '',
    /**
     * 外观定制选装
     */
    [MultipleKeyName.exteriorCustom]: {},
    /**
     * 当前选中的内饰颜色
     */
    F00004: '',
    /**
     * 当前选中的内饰材质
     */
    F00024: '',
    /**
     * 当前选中的第一把钥匙
     */
    F00020: '',
    /**
     * 内饰定制选装
     */
    [MultipleKeyName.interiorCustom]: {},
    /**
     * 选中的选装列表
     */
    [MultipleKeyName.optionalConfig]: {},
    /**
     * 核心标配选装
     */
    [MultipleKeyName.additionalConfig]: {},
    order_person_type: 1,
};
const _getOptionalKey = (featureCode) => {
    const featureListStore = useFeatureListStore();
    const optionKey = Object.entries(featureListStore.optionalFeatureCodes).find(([_key, values]) => values?.includes(featureCode))?.[0];
    return optionKey;
};
const getCurrentOptions = (options, featureCodeMap) => {
    if (!options)
        return options;
    return Object.entries(options).reduce((result, item) => {
        const [key, val] = item;
        if (typeof val !== 'object') {
            const opt = featureCodeMap[key];
            if (!opt) {
                result[key] = val;
                return result;
            }
            if (!!opt.find((op) => op.code === val)) {
                result[key] = val;
            }
            else {
                result[key] = opt.find((op) => op.selected)?.code;
            }
        }
        else {
            result[key] = getCurrentOptions(val, featureCodeMap);
        }
        return result;
    }, {});
};
export const useGlobalStore = defineStore('config-global', {
    state() {
        return {
            showMode: '3D',
            showAllPage: false,
            hasCompleteButton: false,
            flowStatus: {
                /**
                 * 当前执行到的步骤
                 */
                currentStep: [0, 0],
                /**
                 * 当前执行到的一级步骤
                 */
                currentCategory: CategoryCode.VersionBattery,
                /**
                 * 当前执行到的子步骤
                 */
                currentSubCategory: SubCategoryCode.Version,
                /**
                 * 当前变更配置所在 step
                 */
                currentConfigStep: undefined,
            },
            newVersion: undefined,
            currentOpts: JSON.parse(JSON.stringify(initCurrentOpts)),
            orderNo: undefined,
            confNo: undefined,
            toggleGeneration: true,
            currentWebPageName: '选择车型页',
            // 按页面路径存储已曝光的埋点
            exposureMap: {},
        };
    },
    getters: {
        getCurrentWebPageName() {
            return this.currentWebPageName;
        },
        currentMode() {
            return this.showMode;
        },
        // 主要为了区分web端的方法
        isOptionalOriginal() {
            return (this.flowStatus.currentCategory === 'optional' ||
                this.flowStatus.currentCategory === 'additional');
        },
        _featureOptionMap() {
            const featureListStore = useFeatureListStore();
            return featureListStore.data?.option_map;
        },
        currentOptions() {
            const featureListStore = useFeatureListStore();
            const opt = { ...this.currentOpts };
            const result = getCurrentOptions(opt, featureListStore.featureCodeMap);
            return result;
        },
        currentFlatOptions() {
            const opts = this.currentOpts;
            return Object.keys(opts).reduce((result, key) => {
                if (keyNameList.includes(key) &&
                    typeof this.currentOpts[key] === 'object') {
                    Object.keys(this.currentOpts[key]).forEach((k) => {
                        if (isIn(k, this.currentOpts[key])) {
                            result[k] = this.currentOpts[key][k];
                        }
                    });
                }
                else if (!_getOptionalKey(key)) {
                    ;
                    result[key] = this.currentOpts[key];
                }
                return result;
            }, {});
        },
        current3DOptionList() {
            const optionList = [];
            Object.entries(this.currentFlatOptions)
                .filter(([key]) => !['model_code', 'generation_code', 'order_person_type'].includes(key))
                .forEach(([key, value]) => {
                if (typeof value !== 'object') {
                    optionList.push({
                        feature_code: key,
                        option_code: value,
                    });
                }
                else {
                    // 处理定制和选装
                    Object.entries(value).forEach(([key, value]) => {
                        optionList.push({
                            feature_code: key,
                            option_code: value,
                        });
                    });
                }
            });
            return optionList;
        },
        currentOptionList() {
            return Object.entries(this.currentFlatOptions)
                .filter(([key]) => ![
                'model_code',
                'generation_code',
                'order_person_type',
                MultipleKeyName.exteriorCustom,
                MultipleKeyName.interiorCustom,
                MultipleKeyName.optionalConfig,
                MultipleKeyName.additionalConfig,
            ].includes(key))
                .map(([key, value]) => ({
                feature_code: key,
                code: value,
            }));
        },
        currentHideOptions() {
            const featureListStore = useFeatureListStore();
            const hideList = featureListStore.getBatteryVolumeHideOptions;
            const opts = this.currentOptionList;
            const currentOptionCodes = opts
                .map((item) => item.code)
                .filter((item) => !!item);
            return (hideList
                .filter((item) => currentOptionCodes.includes(item.select_option_code))
                .map((item) => item.hide_option_code) || []);
        },
    },
    actions: {
        setCurrentWebPageName(name) {
            this.currentWebPageName = name;
        },
        /**
         * 更新配置：内部方法
         */
        _updateConfig(configs, currentConfigStep) {
            configs.forEach(({ featureCode, code, selected }) => {
                const { optionKey, value } = this._getOptionConfig(featureCode, code);
                const currentOpts = this.currentOpts;
                // const optionConfig =
                if (keyNameList.includes(optionKey)) {
                    if (selected) {
                        currentOpts[optionKey] = {
                            ...currentOpts[optionKey],
                            ...value,
                        };
                    }
                    else {
                        Object.keys(value).forEach((key) => {
                            if (currentOpts[optionKey]?.[key] === value?.[key]) {
                                delete currentOpts[optionKey][key];
                            }
                        });
                        currentOpts[optionKey] = {
                            ...currentOpts[optionKey],
                        };
                    }
                    this.flowStatus.currentConfigStep = currentConfigStep;
                }
                else {
                    if (selected === false) {
                        delete currentOpts[optionKey];
                        this.currentOpts = { ...currentOpts };
                    }
                    else {
                        currentOpts[optionKey] = value;
                    }
                    this.flowStatus.currentConfigStep = currentConfigStep;
                }
            });
        },
        /**
         * 通过 featureCode 获取对应的 currentOption key 及对应的 value
         */
        _getOptionConfig(featureCode, code) {
            const optionKey = _getOptionalKey(featureCode);
            if (optionKey) {
                return {
                    optionKey,
                    value: {
                        [featureCode]: code,
                    },
                };
            }
            return {
                optionKey: featureCode,
                value: code,
            };
        },
        resetCurrentOpts(options) {
            this.currentOpts = Object.assign({}, JSON.parse(JSON.stringify(initCurrentOpts)), options ?? {});
            console.log('---this.currentOpts---', this.currentOpts, options);
        },
        /**
         * 检查互斥逻辑
         * showToast 是否要弹窗
         * 应该不会用到了，先注释了
         */
        async checkRelationships(configs, showToast = true) {
            // if (!showToast) return true
            // const relationConfigs = configs.map(({ code, selected }) => ({
            //   newOptionCode: code,
            //   selected,
            // }))
            // const confirm = await this._relationships(relationConfigs)
            // return confirm.bool !== false
        },
        /**
         * 接口获取配置逻辑
         */
        async _checkRuleShip(configs, loadingCallBack, toastCallBack) {
            if (configs.length == 1) {
                const { optionKey, value } = this._getOptionConfig(configs[0].featureCode, configs[0].code);
                if (['model_code', 'generation_code', 'order_person_type'].includes(optionKey)) {
                    return {
                        bool: true,
                        configList: configs.map((item) => {
                            return {
                                featureCode: item.featureCode,
                                code: item.code,
                            };
                        }),
                    };
                }
            }
            // console.log('---_checkRuleShip--->>>', configs)
            const modalsStore = useModalsStore();
            const ruleCheckStore = useRuleCheckStore();
            const option_list = this.currentOptionList.map((item) => ({
                ...item,
            }));
            const params = {
                model_code: this.currentOptions.model_code,
                generation_code: this.currentOptions.generation_code,
                current_selected_option_list: option_list,
            };
            if (this.newVersion !== undefined) {
                params.new_version = this.newVersion;
            }
            const cancelList = configs.filter((item) => {
                return item.selected === false;
            });
            const selectedList = configs.filter((item) => {
                return item.selected !== false;
            });
            if (cancelList.length) {
                params.new_cancelled_option_list = cancelList.map((item) => {
                    return {
                        feature_code: item.featureCode,
                        code: item.code,
                    };
                });
            }
            if (selectedList.length) {
                params.new_selected_option_list = selectedList.map((item) => {
                    return {
                        feature_code: item.featureCode,
                        code: item.code,
                    };
                });
            }
            loadingCallBack?.showLoading &&
                loadingCallBack?.showLoading({ mask: false });
            try {
                let result = await ruleCheckStore.getRuleCheck(params);
                loadingCallBack?.hideLoading && loadingCallBack?.hideLoading();
                const nonSelectableOptionList = result?.non_selectable_option_list ?? [];
                // //配置切换的toast
                // if (result?.option_change_toast) {
                //   toastCallBack?.showToast?.(result.option_change_toast)
                // }
                // console.log('---result--->>>', result)
                let configList = result?.rule_check_result_option_list.map((item) => {
                    return {
                        featureCode: item.feature_code,
                        code: item.code,
                        selected: true,
                    };
                });
                if (configList?.length) {
                    const currentOptionsList = this.currentOptionList.map((item) => {
                        return {
                            featureCode: item.feature_code,
                            code: item.code,
                            selected: true,
                        };
                    });
                    const newOptions = concatOptionsList(configList, currentOptionsList);
                    configList = newOptions;
                }
                // 优先展示绑定弹窗
                if (result?.option_change_popup) {
                    modalsStore.toggleBundlePopup(true);
                    return { bool: false, };
                }
                if (!result?.rule_check_tip) {
                    return {
                        bool: true,
                        configList: configList,
                        non_selectable_option_list: nonSelectableOptionList,
                    };
                }
                const { close, bool } = await modalsStore.updateRelationModal(true, result?.rule_check_title, result?.rule_check_tip, result?.button_list[0]?.name, result?.button_list[1]?.name ?? '', result?.button_list ?? []);
                close();
                return {
                    bool,
                    configList: configList,
                    rule_check_tip: result?.rule_check_tip,
                    non_selectable_option_list: nonSelectableOptionList,
                };
            }
            catch (error) {
                loadingCallBack?.hideLoading && loadingCallBack?.hideLoading();
                console.log('---error---', error);
                return {
                    bool: false,
                    configList: [],
                };
            }
        },
        /**
         * 更新配置
         */
        async updateConfig(featureCode, code, selected, loadingCallBack, toastCallBack) {
            if (!featureCode)
                return;
            const alreadyOption = this.currentOptionList.filter((item) => {
                return item.feature_code == featureCode && item.code == code && selected;
            });
            if (alreadyOption.length) {
                return;
            }
            const confirmData = await this._checkRuleShip([{ featureCode, code, selected }], loadingCallBack, toastCallBack);
            if (confirmData.bool === false)
                return confirmData;
            this._updateConfig(confirmData?.configList ?? []);
            const featureListStore = useFeatureListStore();
            if (confirmData && 'non_selectable_option_list' in confirmData) {
                featureListStore.refreshOptionDisabledStatus(confirmData.non_selectable_option_list);
            }
            // return confirmData
        },
        /**
         * 批量更新配置
         */
        async batchUpdateConfig(configs, loadingCallBack, showToast = true) {
            console.log('---更新数据---');
            const confirmData = showToast
                ? await this._checkRuleShip(configs, loadingCallBack)
                : null;
            if (confirmData?.bool === false)
                return confirmData;
            const optionsList = confirmData?.configList ?? [];
            this._updateConfig([...configs, ...optionsList]);
            const featureListStore = useFeatureListStore();
            if (confirmData && 'non_selectable_option_list' in confirmData) {
                featureListStore.refreshOptionDisabledStatus(confirmData.non_selectable_option_list);
            }
            return confirmData;
        },
        /**
         * 修改当前步骤
         */
        updateCurrentStep(stepIdx) {
            this.flowStatus.currentStep = stepIdx;
            const featureListStore = useFeatureListStore();
            const currentCategoryData = featureListStore.data?.category_list?.[stepIdx?.[0]];
            if (currentCategoryData?.code) {
                this.flowStatus.currentCategory = currentCategoryData?.code;
            }
            this.flowStatus.currentSubCategory =
                currentCategoryData?.sub_category_list?.[stepIdx?.[1]].code ??
                    [
                        SubCategoryCode.Version,
                        SubCategoryCode.Color,
                        SubCategoryCode.Interior,
                    ][stepIdx?.[0]] ??
                    SubCategoryCode.Version;
            // 当访问的分类不是版本/外观颜色时，标记为首次访问
            const ruleCheckStore = useRuleCheckStore();
            if (ruleCheckStore.isFirstEntry &&
                ![
                    SubCategoryCode.Version,
                    SubCategoryCode.Battery,
                    SubCategoryCode.Color,
                ].includes(this.flowStatus.currentSubCategory)) {
                ruleCheckStore.setIsFirstEntry(false);
            }
        },
        /**
         * 根据feature_type计算出是属于第几步
         */
        featureTypeToStep(feature_type) {
            if (!feature_type)
                return null;
            const featureListStore = useFeatureListStore();
            let subCategoryIndex = 0;
            let categoryIndex = 0;
            let indexs = [0, 0];
            const categoryIndexTemp = featureListStore.data?.category_list.findIndex((category_item) => {
                const subIndex = category_item?.sub_category_list.findIndex((sub_category_item) => {
                    const hasFeatureType = sub_category_item?.feature_list.some((featureItem) => {
                        return featureItem?.feature_type === feature_type;
                    });
                    return hasFeatureType;
                });
                if (subIndex > -1) {
                    subCategoryIndex = subIndex;
                    return true;
                }
                return false;
            });
            if (categoryIndexTemp && categoryIndexTemp > -1) {
                categoryIndex = categoryIndexTemp;
            }
            indexs = [categoryIndex, subCategoryIndex];
            return indexs;
        },
        /**
         * 选择车辆版本
         */
        async updateCurrentVersion(newValue) {
            const { queryFeatureList } = useFeatureListStore();
            try {
                const params = {
                    model_code: this.currentOptions.model_code,
                    generation_code: newValue,
                    need_generation: 1,
                };
                if (this.orderNo) {
                    params.order_no = this.orderNo;
                }
                if (this.newVersion !== undefined) {
                    params.new_version = this.newVersion;
                }
                await queryFeatureList(params);
            }
            catch (err) {
                console.log(err);
            }
        },
        /**
         * 更新当前显示模式，不传参，默认2/3D互切
         */
        updateShowMode(newMode) {
            newMode = newMode ?? (this.showMode === '2D' ? '3D' : '2D');
            this.showMode = newMode;
        },
        /**
         * 配置总览页面
         */
        updateShowAllPage(showAllPage = false) {
            this.showAllPage = showAllPage;
            // 配置总览页面是否首次进入设置为 false
            if (showAllPage) {
                useRuleCheckStore().setIsFirstEntry(false);
            }
        },
        /**
         * 给记录打标
         */
        markerConfig() {
            try {
                markedConfig = JSON.parse(JSON.stringify(this.currentOpts));
            }
            catch (err) {
                console.log(err);
            }
        },
        /**
         * 回滚配置
         */
        rollback() {
            this.currentOpts = markedConfig;
        },
        /**
         * 更新 newVersion
         */
        updateNewVersion(newValue) {
            this.newVersion = newValue;
        },
        /**
         * 更新 orderNo
         */
        updateOrderNo(newValue) {
            console.log('---updateOrderNo---', newValue);
            this.orderNo = newValue;
        },
        /**
         * 更新confNo
         */
        updateConfNo(newValue) {
            console.log('---updateConfNo---', newValue);
            this.confNo = newValue;
        },
        /**
         * 检查曝光状态是否已存在
         */
        hasExposure(pagePath, exposureKey) {
            const pageSet = this.exposureMap[pagePath];
            return pageSet ? pageSet?.has?.(exposureKey) : false;
        },
        /**
         * 添加曝光记录
         */
        addExposure(pagePath, exposureKey) {
            if (!this.exposureMap[pagePath]) {
                this.exposureMap[pagePath] = new Set();
            }
            this.exposureMap[pagePath].add(exposureKey);
        },
        /**
         * 删除单个曝光记录
         */
        removeExposure(pagePath, exposureKey) {
            const pageSet = this.exposureMap[pagePath];
            if (pageSet) {
                pageSet.delete(exposureKey);
            }
        },
    },
    persist: {
        enabled: !!window && !!window.sessionStorage,
        strategies: [
            {
                storage: customStorage,
            },
        ],
    },
});
